import { DatabaseZap, ShieldCheck, FileSignature, Clock } from "lucide-react";
import { Container, Section, Heading, Lede, Eyebrow, LinkButton, FeatureCard, Stat } from "../components/ui";
import { Seo } from "../lib/head";
import { FaqSection, CtaBand, faqPageJsonLd, type Faq } from "../components/marketing";

const FAQS: Faq[] = [
  {
    q: "What is a medication regimen review (MRR)?",
    a: "An MRR (also called a drug regimen review, DRR) is the monthly review a consultant pharmacist performs on each long-term-care resident's medications for appropriateness, dose, duplication, monitoring, and unnecessary drugs — required by CMS under F756/F757. WeConsult Rx runs that review end to end.",
  },
  {
    q: "Does it replace clinical judgment?",
    a: "No. A deterministic rules engine (ASCP, Beers, STOPP/START, GDR) surfaces the flags and AI drafts the rationale, but the pharmacist reviews and finalizes every recommendation. The software removes the busywork, not the decision.",
  },
  {
    q: "Does it integrate with PointClickCare?",
    a: "Yes. Charts are ingested from PointClickCare and other EHRs via FHIR, so each month's data loads without manual re-keying.",
  },
  {
    q: "How does it help with F-tag survey readiness?",
    a: "Every flag, recommendation, prescriber and DON e-signature, and provider response is captured as a dated, defensible record aligned to CMS F-tag guidance (F756, F757, F605, F881).",
  },
  {
    q: "Is it HIPAA-compliant?",
    a: "WeConsult Rx is built for a HIPAA-conscious clinical workflow with a BAA available. Demos use synthetic data only — never real resident PHI.",
  },
];

export function MedicationRegimenReviewSoftware() {
  return (
    <>
      <Seo
        title="Medication Regimen Review Software (MRR) | WeConsult Rx"
        description="Medication regimen review software for consultant pharmacists: chart ingest, ASCP/Beers/STOPP rules and AI flags, prescriber and DON e-sign, and invoicing. Cut MRR time in half."
        path="/medication-regimen-review-software"
        jsonLd={faqPageJsonLd(FAQS)}
      />

      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Medication regimen review software</Eyebrow>
            <Heading level={1} className="mt-3">
              Medication regimen review software, built for consultant pharmacists.
            </Heading>
            <Lede className="mt-6">
              Run the whole monthly MRR from chart ingest to a prescriber-signed, invoiced recommendation —
              in one platform, in about half the time.
            </Lede>
            <div className="mt-8 flex flex-wrap gap-3">
              <LinkButton to="/demo">Book a demo</LinkButton>
              <LinkButton to="/platform" variant="secondary">
                See how it works
              </LinkButton>
            </div>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            <Stat value="~50%" label="Less time per MRR" />
            <Stat value="5" label="F-tags flagged (F756–F881)" />
            <Stat value="1" label="Platform: ingest → sign → invoice" />
          </div>
        </Container>
      </Section>

      <Section tone="soft">
        <Container>
          <Heading level={2}>Everything the monthly review needs</Heading>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <FeatureCard icon={<DatabaseZap className="h-6 w-6" aria-hidden />} title="Charts pulled in automatically">
              PointClickCare and EHR ingest via FHIR loads each resident's regimen — no PDFs to key in by hand.
            </FeatureCard>
            <FeatureCard icon={<Clock className="h-6 w-6" aria-hidden />} title="Rules engine + AI, on every flag">
              ASCP, Beers, STOPP/START and GDR rulepacks surface issues and draft the rationale; you review and finalize.
            </FeatureCard>
            <FeatureCard icon={<ShieldCheck className="h-6 w-6" aria-hidden />} title="Documentation that defends itself">
              Each recommendation ties to a CMS F-tag and a citation, captured as a dated, survey-ready record.
            </FeatureCard>
            <FeatureCard icon={<FileSignature className="h-6 w-6" aria-hidden />} title="E-sign and invoice in the same loop">
              Prescriber and DON e-signature is built in, and an invoice fires the day you close the review.
            </FeatureCard>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <Heading level={2}>How the monthly review runs</Heading>
          <ol className="mt-10 grid gap-8 md:grid-cols-3">
            {[
              { n: "1", t: "Ingest", b: "Pull the month's charts from PointClickCare / EHR via FHIR." },
              { n: "2", t: "Review", b: "Rules + AI flag issues and draft recommendations; you refine and finalize." },
              { n: "3", t: "Sign & bill", b: "Prescriber and DON e-sign the bundle; an invoice goes out automatically." },
            ].map((s) => (
              <li key={s.n} className="rounded-2xl border border-border bg-surface p-6">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-forest-soft font-bold text-forest-700">
                  {s.n}
                </span>
                <Heading level={3} className="mt-4">
                  {s.t}
                </Heading>
                <p className="mt-2 text-[0.9375rem] leading-relaxed text-ink-600">{s.b}</p>
              </li>
            ))}
          </ol>
        </Container>
      </Section>

      <FaqSection items={FAQS} />

      <CtaBand
        title="See it run on a real monthly review"
        body="Book a walkthrough, or start with a free first-facility review to see the time saved on your own charts."
        primary={{ to: "/demo", label: "Book a demo" }}
        secondary={{ to: "/pricing", label: "See pricing" }}
      />
    </>
  );
}
